import { useEffect, useMemo, useRef, useState } from "react";
import Avatar from "./Avatar";
import { api } from "../lib/api";

interface Member {
  id: string;
  email: string;
  name: string | null;
  picture_url: string | null;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  members: Member[];
  placeholder?: string;
  className?: string;
  rows?: number;
  onSubmit?: () => void;
  /** Avatar version for cache-busting (workspace avatar_updated_at etc). */
  avatarVersion?: string | null;
}

// Trigger `@` matches when the user types `@` followed by 0+ name chars at
// the cursor. Allows letters, digits, hyphen, underscore — common slug-like
// names. Whitespace ends the match.
const TRIGGER = /@([A-Za-z0-9_\-\.]*)$/;

// Comment textarea that pops a workspace-member picker when the user types
// `@`. Picking inserts `@<slug>` where slug is the user's name (lowercased,
// spaces → hyphens) or the email's local-part as a fallback. The result is
// plain markdown — rendering is handled by Markdown.tsx.
export default function MentionField({
  value, onChange, members, placeholder, className = "", rows = 4, onSubmit,
}: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [suggest, setSuggest] = useState<{ query: string; start: number; end: number } | null>(null);
  const [hover, setHover] = useState(0);

  const filtered = useMemo(() => {
    if (!suggest) return [];
    const q = suggest.query.toLowerCase();
    if (!q) return members.slice(0, 8);
    return members
      .filter((m) => {
        const slug = memberSlug(m).toLowerCase();
        const name = (m.name ?? "").toLowerCase();
        const email = m.email.toLowerCase();
        return slug.includes(q) || name.includes(q) || email.includes(q);
      })
      .slice(0, 8);
  }, [members, suggest]);

  useEffect(() => { setHover(0); }, [suggest?.query]);

  const updateSuggest = (el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? 0;
    if (el.selectionEnd !== caret) { setSuggest(null); return; }
    const before = el.value.slice(0, caret);
    const m = TRIGGER.exec(before);
    if (!m) { setSuggest(null); return; }
    // Don't fire mid-word (e.g. "email@host.com"). Require a whitespace or
    // start-of-line before the `@`.
    const charBefore = before[m.index - 1];
    if (charBefore && /[A-Za-z0-9._-]/.test(charBefore)) { setSuggest(null); return; }
    setSuggest({ query: m[1], start: m.index, end: caret });
  };

  const pick = (member: Member) => {
    if (!suggest) return;
    const el = ref.current;
    if (!el) return;
    const slug = memberSlug(member);
    const before = el.value.slice(0, suggest.start);
    const after = el.value.slice(suggest.end);
    const insert = `@${slug}`;
    const next = before + insert + " " + after;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = suggest.start + insert.length + 1;
      el.setSelectionRange(pos, pos);
      setSuggest(null);
    });
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        rows={rows}
        placeholder={placeholder}
        className={"input input-mono min-h-[80px] " + className}
        onChange={(e) => { onChange(e.target.value); updateSuggest(e.target); }}
        onSelect={(e) => updateSuggest(e.currentTarget)}
        onKeyDown={(e) => {
          if (suggest && filtered.length > 0) {
            if (e.key === "ArrowDown") { e.preventDefault(); setHover((h) => (h + 1) % filtered.length); return; }
            if (e.key === "ArrowUp")   { e.preventDefault(); setHover((h) => (h - 1 + filtered.length) % filtered.length); return; }
            if (e.key === "Tab" || e.key === "Enter") {
              e.preventDefault();
              pick(filtered[hover]);
              return;
            }
            if (e.key === "Escape") { e.preventDefault(); setSuggest(null); return; }
          }
          // Cmd/Ctrl+Enter submits even when popover isn't open.
          if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSubmit();
          }
        }}
        onBlur={() => setTimeout(() => setSuggest(null), 100)}
      />
      {suggest && filtered.length > 0 && (
        <div
          role="listbox"
          className="absolute left-0 bottom-full mb-1 z-30 card shadow-[var(--shadow-strong)] p-1 text-sm w-72 max-h-64 overflow-y-auto scroll-pane"
        >
          {filtered.map((m, i) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={i === hover}
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              onMouseEnter={() => setHover(i)}
              className={
                "w-full text-left px-2 py-1.5 rounded flex items-center gap-2 " +
                (i === hover ? "bg-surface-2" : "")
              }
            >
              <Avatar
                src={m.picture_url ?? (m.id ? api.avatars.userUrl(m.id) : null)}
                name={m.name}
                email={m.email}
                size={24}
              />
              <span className="flex-1 min-w-0 truncate">
                <span className="font-medium">{m.name ?? m.email}</span>
                <span className="muted-strong text-xs ml-1">@{memberSlug(m)}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Build the slug used as the textual mention (`@slug`). Prefer the name,
// fall back to the email local-part. Lowercased, spaces → hyphens.
export function memberSlug(m: Member): string {
  if (m.name && m.name.trim()) return m.name.trim().toLowerCase().replace(/\s+/g, "-");
  return m.email.split("@")[0].toLowerCase();
}
